import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Snackbar
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { firestore } from '../../firebase/config';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  section: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  listItem: {
    padding: theme.spacing(1, 0),
  },
  listItemLabel: {
    fontWeight: 'bold',
    marginRight: theme.spacing(2),
  },
}));

const CompanyDetailPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchCompany();
  }, [id]);

  const fetchCompany = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const companyDoc = doc(firestore, 'companies', id);
      const companySnapshot = await getDoc(companyDoc);
      
      if (companySnapshot.exists()) {
        setCompany({
          id: companySnapshot.id,
          ...companySnapshot.data()
        });
      } else {
        // Si l'entreprise n'existe pas, utiliser des données de démonstration
        setCompany({
          id: id,
          name: 'Voyage Express',
          email: 'contact@voyage-express.com',
          phone: '+33123456789',
          address: '123 Rue de Paris',
          city: 'Paris',
          postalCode: '75001',
          country: 'France',
          website: 'www.voyage-express.com',
          description: 'Entreprise de transport de voyageurs spécialisée dans les trajets longue distance. Nous proposons des services de qualité avec une flotte moderne et confortable.',
          status: 'Active',
          createdAt: new Date().toISOString(),
          vehicleCount: 15,
          employeeCount: 45,
          yearFounded: 2010
        });
      }
    } catch (error) {
      console.error('Error fetching company:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données de l\'entreprise',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/admin/companies/${id}/edit`);
  };

  const handleDelete = async () => {
    try {
      // Simulation de suppression dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      await deleteDoc(doc(firestore, 'companies', id));
      
      setSnackbar({
        open: true,
        message: 'Entreprise supprimée avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste après un court délai
      setTimeout(() => {
        navigate('/admin/companies');
      }, 1500);
      
    } catch (error) {
      console.error('Error deleting company:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la suppression de l\'entreprise',
        severity: 'error'
      });
    }
  };

  const handleBack = () => {
    navigate('/admin/companies');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'primary';
      case 'Inactive':
        return 'default';
      case 'Pending':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Date invalide';
    }
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        style={{ marginBottom: 16 }}
      >
        Retour à la liste
      </Button>
      
      <Typography variant="h4" component="h1" className={classes.title}>
        Détails de l'entreprise
      </Typography>
      
      {loading ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : company ? (
        <>
          <Paper className={classes.paper}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h5" component="h2">
                  {company.name}
                </Typography>
                <Chip 
                  label={company.status} 
                  color={getStatusColor(company.status)} 
                  className={classes.chip}
                />
                {company.yearFounded && (
                  <Chip 
                    label={`Fondée en ${company.yearFounded}`} 
                    variant="outlined" 
                    className={classes.chip}
                  />
                )}
              </Grid>
              
              <Grid item xs={12}>
                <Divider className={classes.divider} />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Informations de contact
                </Typography>
                <List>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Email:</Box>
                          <Box>{company.email}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Téléphone:</Box>
                          <Box>{company.phone}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Site web:</Box>
                          <Box>{company.website || 'Non renseigné'}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Adresse
                </Typography>
                <List>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Rue:</Box>
                          <Box>{company.address}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Ville:</Box>
                          <Box>{company.city || 'Non renseignée'}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Code postal:</Box>
                          <Box>{company.postalCode || 'Non renseigné'}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Pays:</Box>
                          <Box>{company.country}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12}>
                <Divider className={classes.divider} />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Description
                </Typography>
                <Typography variant="body1" paragraph>
                  {company.description || 'Aucune description disponible.'}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Divider className={classes.divider} />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Statistiques
                </Typography>
                <List>
                  {company.vehicleCount !== undefined && (
                    <ListItem className={classes.listItem}>
                      <ListItemText 
                        primary={
                          <Box display="flex">
                            <Box className={classes.listItemLabel}>Nombre de véhicules:</Box>
                            <Box>{company.vehicleCount}</Box>
                          </Box>
                        } 
                      />
                    </ListItem>
                  )}
                  {company.employeeCount !== undefined && (
                    <ListItem className={classes.listItem}>
                      <ListItemText 
                        primary={
                          <Box display="flex">
                            <Box className={classes.listItemLabel}>Nombre d'employés:</Box>
                            <Box>{company.employeeCount}</Box>
                          </Box>
                        } 
                      />
                    </ListItem>
                  )}
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Créée le:</Box>
                          <Box>{formatDate(company.createdAt)}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
            
            <div className={classes.buttons}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
              >
                Supprimer
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Modifier
              </Button>
            </div>
          </Paper>
        </>
      ) : (
        <Paper className={classes.paper}>
          <Typography variant="body1">
            Entreprise non trouvée
          </Typography>
        </Paper>
      )}
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyDetailPage;
